import { PLATFORMS } from "@amtana/analysis-contract";
import type { ANALYSIS_ERROR_CODES, Analysis, Platform, WaitingCode } from "@amtana/analysis-contract";
import type { ErrorCopyFor } from "@amtana/browser";
import { phrase } from "@amtana/i18n/catalog";
import type { Catalogs, Formats, Translate, Translation } from "@amtana/i18n/catalog";

/** A banner's two lines, already in the reader's language. */
export type BannerCopy = { readonly title: string; readonly description?: string };

// What a user reads when an analysis fails (packages/module ADR-001). The
// copy names the platform the user chose, never the service we read it
// through: "Waiting on Instagram", not the provider's name.

export type AnalysisErrorCatalog = typeof EN;

const EN = {
  notFoundTitle: "This analysis doesn't exist",
  notFoundDescription: "It may have been deleted. Open your analyses to pick another.",
  lockedTitle: "This analysis has already started",
  lockedDescription: "Its setup can no longer change. Create a new analysis for a different target.",
  incompleteTitle: "The analysis isn't ready to start",
  incompleteDescription: "Pick a platform, a target and what to extract first.",
  concurrencyTitle: "Another analysis is already running",
  concurrencyDescription: "Your plan runs one analysis at a time.",
  outOfCreditsTitle: "You're out of credits",
  outOfCreditsDescription: "Upgrade your plan or add credits to continue.",
  searchUnavailableTitle: "We can't search right now",
  searchUnavailableDescription: "The platform isn't answering. Try again in a few minutes.",
  pausedTitle: "New analyses are paused for a moment",
  pausedDescription: "Nothing was charged. Try again shortly.",
  platformUnavailableTitle: "This platform is coming soon",
  platformUnavailableDescription: "Nothing was charged. Start an Instagram analysis for now.",
  modeUnavailableTitle: "This source is coming soon",
  modeUnavailableDescription:
    "Nothing was charged. Start an analysis on an account's followers or following for now.",
  // Platforms; why a run stopped is RUN_FAILURE_CATALOGS (run-failure-copy.ts).
  thePlatform: "the platform",
  unnamedPlatform: "the {id} provider",
  // While a scan waits on its platform.
  waitingTitle: "Waiting on {name}",
  waitingDown: "{name} isn't answering right now; the analysis continues on its own.",
  waitingSlow: "{name} is slowing us down; the analysis continues at a lower pace.",
  waitingRefused: "Our access to {name} is paused; the analysis continues once it's back.",
  nextTry: "Next try at {time}.",
  // A run whose work we parked (eventing ADR-001, revision 1).
  heldTitle: "This analysis is on hold on our side",
  heldDescription:
    "Something keeps failing on our side and we've been told. The contacts already found are kept; it continues on its own once we've fixed it.",
} as const;

const ES: Translation<AnalysisErrorCatalog> = {
  notFoundTitle: "Este análisis no existe",
  notFoundDescription: "Puede que lo hayan eliminado. Abre tus análisis para elegir otro.",
  lockedTitle: "Este análisis ya empezó",
  lockedDescription: "Su configuración ya no puede cambiar. Crea un análisis nuevo para otro objetivo.",
  incompleteTitle: "El análisis no está listo para empezar",
  incompleteDescription: "Primero elige una plataforma, un objetivo y qué extraer.",
  concurrencyTitle: "Ya hay otro análisis en curso",
  concurrencyDescription: "Tu plan ejecuta un análisis a la vez.",
  outOfCreditsTitle: "Te quedaste sin créditos",
  outOfCreditsDescription: "Mejora tu plan o agrega créditos para continuar.",
  searchUnavailableTitle: "No podemos buscar ahora",
  searchUnavailableDescription: "La plataforma no responde. Inténtalo de nuevo en unos minutos.",
  pausedTitle: "Los análisis nuevos están en pausa un momento",
  pausedDescription: "No se cobró nada. Inténtalo de nuevo en un rato.",
  platformUnavailableTitle: "Esta plataforma llega pronto",
  platformUnavailableDescription: "No se cobró nada. Por ahora, empieza un análisis de Instagram.",
  modeUnavailableTitle: "Esta fuente llega pronto",
  modeUnavailableDescription:
    "No se cobró nada. Por ahora, empieza un análisis sobre los seguidores o seguidos de una cuenta.",
  thePlatform: "la plataforma",
  unnamedPlatform: "el proveedor {id}",
  waitingTitle: "Esperando a {name}",
  waitingDown: "{name} no responde por ahora; el análisis continúa por su cuenta.",
  waitingSlow: "{name} nos está frenando; el análisis continúa a menor ritmo.",
  waitingRefused: "Nuestro acceso a {name} está en pausa; el análisis continúa cuando vuelva.",
  nextTry: "Próximo intento a las {time}.",
  heldTitle: "Este análisis está en pausa por nuestra parte",
  heldDescription:
    "Algo sigue fallando de nuestro lado y ya nos avisaron. Se conservan los contactos ya encontrados; continúa por su cuenta cuando lo arreglemos.",
};

export const ANALYSIS_ERROR_CATALOGS: Catalogs<AnalysisErrorCatalog> = { en: EN, es: ES };

export const ANALYSIS_ERROR_COPY = {
  analysis_not_found: {
    title: phrase(ANALYSIS_ERROR_CATALOGS, "notFoundTitle"),
    description: phrase(ANALYSIS_ERROR_CATALOGS, "notFoundDescription"),
  },
  analysis_locked: {
    title: phrase(ANALYSIS_ERROR_CATALOGS, "lockedTitle"),
    description: phrase(ANALYSIS_ERROR_CATALOGS, "lockedDescription"),
  },
  analysis_incomplete: {
    title: phrase(ANALYSIS_ERROR_CATALOGS, "incompleteTitle"),
    description: phrase(ANALYSIS_ERROR_CATALOGS, "incompleteDescription"),
  },
  analysis_concurrency_limit: {
    title: phrase(ANALYSIS_ERROR_CATALOGS, "concurrencyTitle"),
    description: phrase(ANALYSIS_ERROR_CATALOGS, "concurrencyDescription"),
  },
  analysis_out_of_credits: {
    title: phrase(ANALYSIS_ERROR_CATALOGS, "outOfCreditsTitle"),
    description: phrase(ANALYSIS_ERROR_CATALOGS, "outOfCreditsDescription"),
  },
  analysis_search_unavailable: {
    title: phrase(ANALYSIS_ERROR_CATALOGS, "searchUnavailableTitle"),
    description: phrase(ANALYSIS_ERROR_CATALOGS, "searchUnavailableDescription"),
  },
  // ADR-006 §3/§6, S8.05: the "analysis-start" kill switch, switched off.
  analysis_paused: {
    title: phrase(ANALYSIS_ERROR_CATALOGS, "pausedTitle"),
    description: phrase(ANALYSIS_ERROR_CATALOGS, "pausedDescription"),
  },
  analysis_platform_unavailable: {
    title: phrase(ANALYSIS_ERROR_CATALOGS, "platformUnavailableTitle"),
    description: phrase(ANALYSIS_ERROR_CATALOGS, "platformUnavailableDescription"),
  },
  analysis_mode_unavailable: {
    title: phrase(ANALYSIS_ERROR_CATALOGS, "modeUnavailableTitle"),
    description: phrase(ANALYSIS_ERROR_CATALOGS, "modeUnavailableDescription"),
  },
} satisfies ErrorCopyFor<typeof ANALYSIS_ERROR_CODES>;

type ErrorTranslate = Translate<AnalysisErrorCatalog>;

/** The platform as the user knows it; a platform without a name of its own is "the <id> provider". */
export function platformName(platform: Platform, t: ErrorTranslate): string {
  return PLATFORMS.find((info) => info.id === platform)?.name ?? t("unnamedPlatform", { id: platform });
}

/** What the progress panel says while a scan waits on its platform, or is held on our side. */
export function waitingCopy(
  waiting: NonNullable<Analysis["waiting"]>,
  params: { t: ErrorTranslate; format: Formats },
): BannerCopy {
  const { t, format } = params;
  if (waiting.code === "run-stalled") return { title: t("heldTitle"), description: t("heldDescription") };
  const name = platformName(waiting.platform, t);
  const next = waiting.retryAt ? ` ${t("nextTry", { time: format.time(waiting.retryAt) })}` : "";
  const why: Readonly<Record<Exclude<WaitingCode, "run-stalled">, string>> = {
    "provider-down": t("waitingDown", { name }),
    "provider-slow": t("waitingSlow", { name }),
    "provider-refused": t("waitingRefused", { name }),
  };
  return { title: t("waitingTitle", { name }), description: `${why[waiting.code]}${next}` };
}
